import ManoirSection from "@/components/ManoirSection";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Compass, Feather, Moon, HeartHandshake, Mic2, Clapperboard, Sparkles, CalendarDays, Users, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { useSettings } from "@/lib/SettingsContext";
import { useAvailability } from "@/lib/ContentContext";
import { useRdv } from "@/components/SiteExtras";
import { track } from "@/lib/analytics";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import BookCard from "@/components/BookCard";
import SocialSection from "@/components/SocialSection";
import Recommended from "@/components/Recommended";
import IntroOverlay from "@/components/IntroOverlay";
import DoorsSection from "@/components/DoorsSection";
import Timeline from "@/components/Timeline";

const QUICK = [
  { to: "/livres", label: "Mes Livres", Icon: BookOpen, span: "md:col-span-2 md:row-span-2", img: IMG.booksShelf },
  { to: "/les-chroniques-de-letrange", label: "Les Chroniques de l'Étrange", Icon: Compass, span: "md:col-span-2", img: IMG.gothic },
  { to: "/conferences", label: "Mes Conférences", Icon: Mic2 },
  { to: "/consultations", label: "Mes Consultations", Icon: Sparkles },
  { to: "/onirotherapie", label: "Onirothérapie", Icon: Moon },
  { to: "/mediumnite", label: "Médiumnité", Icon: Feather },
  { to: "/kit-medias", label: "Presse & Médias", Icon: Clapperboard, coll: "press" },
  { to: "/agenda", label: "Agenda", Icon: CalendarDays, coll: "events" },
  { to: "/aaesad", label: "AAESAD", Icon: HeartHandshake },
];

const UNIVERS = [
  { title: "Auteure", text: "Une bibliographie qui traverse l'ésotérisme, le deuil, la poésie, le développement personnel et la fiction.", to: "/livres" },
  { title: "Histoire & Ésotérisme", text: "Comprendre l'origine des pratiques, leur contexte et ce que les sociétés en ont fait.", to: "/histoire-esoterisme" },
  { title: "Médiumnité", text: "Une démarche nuancée, respectueuse, entre intuition, écoute et transmission.", to: "/mediumnite" },
  { title: "Onirothérapie", text: "Un travail autour du rêve, du symbole et de l'inconscient, d'inspiration jungienne.", to: "/onirotherapie" },
  { title: "Deuil", text: "Un accompagnement de l'absence, du manque et du lien qui continue.", to: "/deuil" },
  { title: "Conférences", text: "Salons, rencontres et interventions publiques sur l'histoire de l'invisible.", to: "/conferences" },
  { title: "Création de contenus", text: "Des vidéos, des récits et des projets qui ont rencontré des publics très différents.", to: "/videos", coll: "videos" },
];

const INTENTS = [
  { key: "consulter", label: "Je souhaite consulter", desc: "Voyance, médiumnité, onirothérapie", Icon: Sparkles, to: "/consultations" },
  { key: "livres", label: "Découvrir ses livres", desc: "Romans, essais, poésie, ésotérisme", Icon: BookOpen, to: "/livres" },
  { key: "evenement", label: "La rencontrer", desc: "Salons, dédicaces, agenda", Icon: CalendarDays, to: "/agenda", coll: "events" },
  { key: "conference", label: "L'inviter en conférence", desc: "Salons, écoles, rencontres", Icon: Mic2, demande: "conference" },
  { key: "media", label: "Journaliste / média", desc: "Espace presse & kit média", Icon: Clapperboard, to: "/kit-media" },
  { key: "partenariat", label: "Proposer un partenariat", desc: "Collaboration, projet commun", Icon: HeartHandshake, demande: "partenariat" },
  { key: "univers", label: "Découvrir son univers", desc: "Chroniques de l'Étrange, vidéos", Icon: Compass, to: "/univers" },
];

export default function Home() {
  const settings = useSettings();
  const avail = useAvailability();
  const hasC = (o) => !o.coll || (avail?.[o.coll] || 0) > 0;
  const quick = QUICK.filter(hasC);
  const univers = UNIVERS.filter(hasC);
  const intents = INTENTS.filter(hasC);
  const { openDemande } = useRdv() || {};
  const { data: books = [] } = useQuery({ queryKey: ["books"], queryFn: () => getList("books") });
  const intro = settings?.hero_intro || "";

  useSeo({
   title: "Dorothée Lancelot | Médium, auteure & conférencière",
description: "Découvrez l’univers de Dorothée Lancelot : médiumnité, deuil, rêves, livres, conférences, onirothérapie, ésotérisme et Les Chroniques de l’Étrange.",
    path: "/",
    keywords: ["Dorothée Lancelot", "médium", "voyance", "consultation médium", "contact avec un défunt", "onirothérapie", "accompagnement du deuil", "auteure", "conférencière", "Les Chroniques de l'Étrange", "ésotérisme", "spiritualité", "prendre rendez-vous médium"],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Dorothée Lancelot",
      jobTitle: "Auteure, Médium, Conférencière, Onirothérapeute",
      description: "Auteure, conférencière et créatrice de contenus autour de l'ésotérisme, de son histoire, du deuil, de la médiumnité et du rêve.",
    },
  });

  return (
    <div>
      <IntroOverlay />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center dl-noise overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.heroLibrary} fectchPriority="high" alt="Bibliothèque ancienne à la lumière chaude" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(5,8,20,0.94) 0%, rgba(5,8,20,0.8) 45%, rgba(5,8,20,0.55) 100%)" }} />
        </div>
        <div className="dl-container relative z-10 py-32">
          <div className="dl-eyebrow mb-6 dl-fade">Site officiel</div>
          <h1 className="serif text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-[#F3E9D2] dl-fade" style={{ animationDelay: ".1s" }}>
            DOROTHÉE<br />LANCELOT
          </h1>
          <p className="mt-6 text-[#D4B978] tracking-[0.2em] uppercase text-sm dl-fade" style={{ animationDelay: ".2s" }}>
            Auteure • Médium • Conférencière • Onirothérapeute
          </p>
          <div className="mt-8 max-w-2xl space-y-3 dl-fade" style={{ animationDelay: ".3s" }}>
            {intro.split("\n").map((line, i) => (
              <p key={i} className="text-[#F3E9D2]/80 text-lg leading-relaxed">{line}</p>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-10 dl-fade" style={{ animationDelay: ".4s" }}>
            <Link to="/livres" data-testid="hero-books-btn" className="dl-btn dl-btn-solid">Découvrir mes livres</Link>
            <Link to="/les-chroniques-de-letrange" data-testid="hero-chroniques-btn" className="dl-btn">Explorer les Chroniques</Link>
          </div>
        </div>
      </section>

      {/* INTENT ROUTER — le visiteur est le point de départ (test des 10 secondes) */}
      <section id="parcours" className="border-b border-[rgba(198,167,106,0.18)] bg-[#0B1020]">
        <div className="dl-container py-16 md:py-20">
          <div className="dl-eyebrow mb-3">Trouvez votre chemin</div>
          <h2 className="serif text-3xl md:text-5xl text-[#F3E9D2] max-w-3xl">Que recherchez-vous aujourd'hui&nbsp;?</h2>
          <p className="text-[#F3E9D2]/60 mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
            Un seul espace pour découvrir, consulter, lire, rencontrer ou inviter Dorothée Lancelot.
            Choisissez votre intention, on vous conduit directement au bon endroit.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {intents.map((it) => {
              const inner = (
                <>
                  <it.Icon size={22} className="text-[#C6A76A] shrink-0 mt-0.5" />
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="serif text-lg text-[#F3E9D2] group-hover:text-[#D4B978] transition-colors leading-tight">{it.label}</span>
                      <ArrowRight size={16} className="text-[#C6A76A]/60 group-hover:text-[#C6A76A] group-hover:translate-x-1 transition-all shrink-0" />
                    </span>
                    <span className="block text-xs text-[#F3E9D2]/50 mt-1.5">{it.desc}</span>
                  </span>
                </>
              );
      const premium = [
"/consultations",
"/conferences",
"/presse-medias",
"/les-chroniques-de-letrange",
].includes(it.to);
              const cls = "group flex gap-3 border border-[rgba(198,167,106,0.22)] p-5 hover:border-[#C6A76A] hover:bg-white/[0.03] transition-colors premium-card premium-reveal";
              return it.demande ? (
                <button
                  key={it.key}
                  data-testid={`intent-${it.key}`}
                  onClick={() => { track("concierge_intent", { intent: it.key, source: "home" }); openDemande?.(it.demande); }}
                 className={`${cls} ${premium ? "ring-1 ring-[#C6A76A]/30 shadow-[0_18px_50px_rgba(0,0,0,0.18)]" : ""}`}
                >{inner}</button>
              ) : (
                <Link
                  key={it.key}
                  to={it.to}
                  data-testid={`intent-${it.key}`}
                  onClick={() => track("concierge_intent", { intent: it.key, source: "home" })}
                  className={cls}
                >{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMMERSIVE DOORS — priority experience for first-time visitors */}
      <DoorsSection />

      {/* NOTORIETY BAND — facts, not adjectives */}
      <section className="border-y border-[rgba(198,167,106,0.18)] bg-[#0B1020]">
        <div className="dl-container py-16 md:py-20">
          <p className="serif text-2xl md:text-4xl text-[#F3E9D2] leading-snug max-w-4xl">
            Dorothée Lancelot ne s'inscrit pas dans une seule case. Son nom s'est construit autour de plusieurs univers :
            <span className="text-[#D4B978]"> l'écriture, la transmission, l'ésotérisme, l'histoire, le rêve et l'accompagnement du deuil.</span>
          </p>
          <p className="mt-6 text-[#F3E9D2]/65 max-w-3xl leading-relaxed">
            Des ouvrages publiés, des conférences, des interventions, des projets personnels et une communauté qui s'est
            développée au fil du temps. Ses contenus ont rencontré des publics très différents, parfois à grande échelle.
            Son travail a également fait l'objet d'interviews, de passages radio et d'articles de presse.
          </p>
        </div>
      </section>

      {/* QUICK ACCESS BENTO */}
      <section className="dl-container py-20 md:py-28">
        <div className="mb-12">
          <div className="dl-eyebrow mb-3">Explorer l'univers</div>
          <h2 className="serif text-4xl md:text-5xl text-[#F3E9D2]">Plusieurs univers, une même curiosité.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[150px] gap-5">
          {quick.map(({ to, label, Icon, span = "", img }) => (
            <Link
              key={to}
              to={to}
              data-testid={`quick-${to.replace(/\//g, "")}`}
              className={`dl-card group relative overflow-hidden flex flex-col justify-end p-6 ${span}`}
            >
              {img && (
                <>
                  <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-45 transition-opacity duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020] via-[#0B1020]/60 to-transparent" />
                </>
              )}
              <Icon size={22} className="text-[#C6A76A] mb-3 relative z-10" />
              <span className="serif text-xl md:text-2xl text-[#F3E9D2] leading-tight relative z-10 group-hover:text-[#D4B978] transition-colors">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* UNIVERS BLOCKS */}
      <section className="dl-container pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {univers.map((u, i) => (
            <Link to={u.to} key={i} className="dl-card p-8 group">
              <span className="dl-eyebrow" style={{ fontSize: "0.6rem" }}>0{i + 1}</span>
              <h3 className="serif text-2xl text-[#F3E9D2] mt-3 group-hover:text-[#D4B978] transition-colors">{u.title}</h3>
              <p className="text-sm text-[#F3E9D2]/65 mt-3 leading-relaxed">{u.text}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* BOOKS PREVIEW */}
      <section className="dl-container py-20 md:py-28">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="dl-eyebrow mb-3">La bibliothèque</div>
            <h2 className="serif text-4xl md:text-5xl text-[#F3E9D2]">Les livres de Dorothée Lancelot</h2>
          </div>
          <Link to="/livres" className="dl-link text-sm">Voir tous les livres →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {books.slice(0, 4).map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      </section>

      {/* CHRONIQUES TEASER */}
      <section className="relative dl-noise overflow-hidden border-y border-[rgba(198,167,106,0.18)]">
        <div className="absolute inset-0">
          <img src={IMG.scotland} loading="lazy" alt="Landes au coucher du soleil" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #050814 20%, rgba(5,8,20,0.6) 100%)" }} />
        </div>
        <div className="dl-container relative z-10 py-24 max-w-3xl">
          <div className="dl-eyebrow mb-4">Projet</div>
          <h2 className="serif text-4xl md:text-6xl text-[#F3E9D2] leading-tight">Les Chroniques de l'Étrange</h2>
          <p className="mt-6 text-[#F3E9D2]/75 text-lg leading-relaxed">
            Une exploration documentée de l'histoire de la spiritualité, du mysticisme, de l'occultisme, des arts
            divinatoires et des grandes légendes.
          </p>
          <p className="serif italic text-xl text-[#D4B978] mt-6">
            « Ici, on distingue toujours ce que l'on sait, ce que l'on croit et ce que l'on raconte. »
          </p>
          <Link to="/les-chroniques-de-letrange" className="dl-btn mt-8">Découvrir les Chroniques</Link>
        </div>
      </section>

      {/* SOCIAL */}
      <ManoirSection />
      <SocialSection />

      {/* PARCOURS / CREDIBILITY */}
      <Timeline />

      {/* RECOMMENDED */}
      <Recommended />

      {/* FINAL BLOCK */}
      <section className="dl-container py-28 text-center">
        <Users size={26} className="text-[#C6A76A] mx-auto mb-6" />
        <p className="serif text-3xl md:text-5xl text-[#F3E9D2] leading-tight max-w-4xl mx-auto">
          Plus qu'un univers ésotérique, un travail de transmission, de recherche, d'écriture et de mémoire.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-12">
          <Link to="/livres" className="dl-btn dl-btn-solid">Découvrir mes livres</Link>
          <Link to="/les-chroniques-de-letrange" className="dl-btn">Explorer les Chroniques de l'Étrange</Link>
        </div>
      </section>
    </div>
  );
}
